import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Check, Loader2, SwitchCamera, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SIZE = 640;

export const CameraCapture = ({ open, onOpenChange, onCapture, busy }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [error, setError] = useState("");
  const [facing, setFacing] = useState("user");
  const [ready, setReady] = useState(false);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
  };

  useEffect(() => {
    if (!open) {
      stop();
      setShot(null);
      setError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setError("");
      stop();
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Camera access was blocked. Allow camera permission for talentahela.com in your browser settings and try again."
            : "No camera available on this device. Your One Talanta ID photo must be taken live."
        );
      }
    })();
    return () => {
      cancelled = true;
      stop();
    };
  }, [open, facing]);

  const capture = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const side = Math.min(v.videoWidth, v.videoHeight);
    const c = document.createElement("canvas");
    c.width = c.height = SIZE;
    const ctx = c.getContext("2d");
    if (facing === "user") {
      ctx.translate(SIZE, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, SIZE, SIZE);
    setShot(c.toDataURL("image/jpeg", 0.9));
  };

  const use = async () => {
    const blob = await (await fetch(shot)).blob();
    await onCapture(new File([blob], "one-talanta-id-photo.jpg", { type: "image/jpeg" }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="camera-capture-dialog" className="sm:max-w-sm p-0 overflow-hidden border-0 bg-talanta-green text-white">
        <DialogTitle className="sr-only">Take your ID photo</DialogTitle>
        <DialogDescription className="sr-only">Use your camera to take a live photo for your One Talanta ID.</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="px-5 pt-5">
          <div className="flex items-center gap-2 text-talanta-gold text-xs font-semibold uppercase tracking-widest">
            <Camera className="h-4 w-4" /> Live ID photo
          </div>
          <h2 className="mt-1 font-display font-bold text-xl">{shot ? "Looking good?" : "Centre your face in the frame"}</h2>
          <p className="mt-1 text-sm text-white/65">Photos are taken live with your camera — uploads aren't allowed, so every ID is verified as real.</p>
        </div>
        <div className="p-5">
          <div className="relative aspect-square w-full rounded-2xl overflow-hidden bg-black">
            <video
              ref={videoRef}
              data-testid="camera-video"
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              style={{ transform: facing === "user" ? "scaleX(-1)" : "none", visibility: shot ? "hidden" : "visible" }}
            />
            {shot && (
              <img data-testid="camera-shot-preview" src={shot} alt="Your ID photo" className="absolute inset-0 h-full w-full object-cover" />
            )}
            {!shot && !error && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-[72%] w-[72%] rounded-full border-2 border-dashed border-talanta-gold/70" />
              </div>
            )}
            {!shot && !error && !ready && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Loader2 className="h-8 w-8 animate-spin text-talanta-gold" /></div>
            )}
            {error && (
              <div data-testid="camera-error" className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-talanta-hover">
                <AlertTriangle className="h-8 w-8 text-talanta-gold" />
                <p className="text-sm text-white/80">{error}</p>
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-[auto_1fr_auto] items-center gap-3">
            {shot ? (
              <>
                <button data-testid="camera-retake-btn" onClick={() => setShot(null)} disabled={busy} className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Retake">
                  <RefreshCw className="h-5 w-5" />
                </button>
                <button
                  data-testid="camera-use-photo-btn"
                  onClick={use}
                  disabled={busy}
                  className="h-12 rounded-full bg-talanta-red hover:bg-talanta-red/90 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5" /> Use this photo</>}
                </button>
                <span className="w-12" />
              </>
            ) : (
              <>
                <span className="w-12" />
                <button
                  data-testid="camera-shutter-btn"
                  onClick={capture}
                  disabled={!ready || !!error}
                  className="mx-auto h-16 w-16 rounded-full border-4 border-white/80 bg-talanta-red hover:bg-talanta-red/90 disabled:opacity-40 flex items-center justify-center"
                  aria-label="Take photo"
                >
                  <Camera className="h-6 w-6" />
                </button>
                <button
                  data-testid="camera-switch-btn"
                  onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
                  disabled={!!error}
                  className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-40"
                  aria-label="Switch camera"
                >
                  <SwitchCamera className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
